/**
 * Product store cart — localStorage-backed.
 *
 * Cart lives entirely on the client until checkout submit. Add, update,
 * remove are pure FE operations: no API round-trips, no DB rows for
 * abandoned carts, no navbar API ping on page load. The cart items are
 * sent in the `place-order` request body when the user submits.
 *
 * The slice still exposes the same action names (fetchCartAction,
 * addToCartAction, updateCartItemAction, removeCartItemAction) the rest
 * of the FE expects — but they're synchronous mutations against the slice
 * + localStorage rather than thunks that hit the server.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CartItem, CartItemProduct } from "../api/fetchCart/types";
import { ProductStoreProduct } from "../api/fetchProductStoreProducts/types";
import { AppDispatch, AppThunk } from "@/app/store";

const STORAGE_KEY = "productStoreCart:v1";

interface ProductStoreCartState {
  items: CartItem[];
  totalCount: number;
  loading: boolean;
}

const isBrowser = typeof window !== "undefined" && !!window.localStorage;

const readFromStorage = (): CartItem[] => {
  if (!isBrowser) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as CartItem[];
  } catch {
    return [];
  }
};

const writeToStorage = (items: CartItem[]): void => {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Quota / private mode — silently drop. Cart is best-effort persistent.
  }
};

const hydrated = readFromStorage();

const initialState: ProductStoreCartState = {
  items: hydrated,
  totalCount: hydrated.length,
  loading: false,
};

const productAsCartItemProduct = (
  product: ProductStoreProduct,
): CartItemProduct => ({
  id: product.id,
  name: product.name,
  description: product.description,
  type: product.type,
  price: product.price,
  thumbnailUrl: product.thumbnailUrl,
  category: { id: product.category.id, name: product.category.name },
});

let lineIdCounter = hydrated.reduce((max, line) => Math.max(max, line.id), 0);
const nextLineId = (): number => {
  lineIdCounter += 1;
  return lineIdCounter;
};

const productStoreCartSlice = createSlice({
  name: "productStoreCart",
  initialState,
  reducers: {
    upsertCartItem: (
      state,
      action: PayloadAction<{ product: ProductStoreProduct; quantity: number }>,
    ) => {
      const { product, quantity } = action.payload;
      const existing = state.items.find((i) => i.product.id === product.id);
      if (existing) {
        existing.quantity += quantity;
        existing.updatedAt = new Date().toISOString();
      } else {
        const now = new Date().toISOString();
        state.items.push({
          id: nextLineId(),
          product: productAsCartItemProduct(product),
          quantity,
          createdAt: now,
          updatedAt: now,
        });
      }
      state.totalCount = state.items.length;
      writeToStorage(state.items);
    },
    setCartItemQuantity: (
      state,
      action: PayloadAction<{ id: number; quantity: number }>,
    ) => {
      const { id, quantity } = action.payload;
      const line = state.items.find((i) => i.id === id);
      if (line) {
        line.quantity = quantity;
        line.updatedAt = new Date().toISOString();
        writeToStorage(state.items);
      }
    },
    removeCartItemFromState: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((i) => i.id !== action.payload);
      state.totalCount = state.items.length;
      writeToStorage(state.items);
    },
    resetCart: (state) => {
      state.items = [];
      state.totalCount = 0;
      writeToStorage([]);
    },
  },
});

export const {
  upsertCartItem,
  setCartItemQuantity,
  removeCartItemFromState,
  resetCart,
} = productStoreCartSlice.actions;

/**
 * No-op kept for source compatibility with callers that still dispatch
 * `fetchCartAction()`. The slice hydrates from localStorage at module
 * load; there's no server fetch to perform anymore.
 */
export const fetchCartAction = (): AppThunk => async () => {
  /* intentionally empty */
};

export const addToCartAction =
  (product: ProductStoreProduct, quantity = 1): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(upsertCartItem({ product, quantity }));
  };

export const updateCartItemAction =
  (id: number, quantity: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    if (quantity < 1) {
      dispatch(removeCartItemFromState(id));
    } else {
      dispatch(setCartItemQuantity({ id, quantity }));
    }
  };

export const removeCartItemAction =
  (id: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(removeCartItemFromState(id));
  };

export default productStoreCartSlice.reducer;
